import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";

export const GITHUB_APP_SLUG = "studyboard-publiser";

export interface GitHubStatus {
    configured: boolean;
    installationId?: number | null;
    repo?: string | null;
    branch?: string | null;
    connectedAs?: string | null;
    availableRepos?: string[];
    repoDescription?: string | null;
    groupOrder?: string[] | null;
    publishing?: boolean;
    lastPublishResult?: 'success' | 'error' | null;
    lastPublishUrl?: string | null;
    lastPublishError?: string | null;
    lastPublishedAt?: number | null;
}

export function getGitHubInstallUrl(): string {
    return `https://github.com/apps/${GITHUB_APP_SLUG}/installations/new`;
}

export async function linkGitHubInstallation(installationId: number): Promise<{ ok: boolean; login?: string; repos?: string[] }> {
    const fn = httpsCallable(functions, 'linkGitHubInstallation');
    const result = await fn({ installationId });
    return result.data as { ok: boolean; login?: string; repos?: string[] };
}

export async function updateGitHubRepoConfig(repo: string, branch: string): Promise<void> {
    const fn = httpsCallable(functions, 'updateGitHubRepoConfig');
    await fn({ repo, branch });
}

export async function saveGitHubRepoDescription(repoDescription: string): Promise<void> {
    const fn = httpsCallable(functions, 'updateGitHubRepoConfig');
    await fn({ repoDescription });
}

export async function saveGroupOrder(groupOrder: string[]): Promise<void> {
    const fn = httpsCallable(functions, 'updateGitHubRepoConfig');
    await fn({ groupOrder });
}

export async function listGitHubRepos(): Promise<string[]> {
    const fn = httpsCallable(functions, 'listGitHubRepos');
    const result = await fn({});
    return (result.data as { repos: string[] }).repos;
}

export async function getPublishedNotebookFolders(): Promise<string[]> {
    const fn = httpsCallable(functions, 'getPublishedNotebookFolders');
    const result = await fn({});
    return (result.data as { folders: string[] }).folders;
}

export async function clearGitHubConfig(): Promise<void> {
    const fn = httpsCallable(functions, 'clearGitHubConfig');
    await fn({});
}

export async function generateRepoDescription(language: 'en' | 'vi' | 'both'): Promise<string> {
    const fn = httpsCallable(functions, 'generateRepoDescription');
    const result = await fn({ language });
    return (result.data as { description: string }).description;
}

export async function refineRepoDescription(existingText: string, hint: string, language: 'en' | 'vi' | 'both'): Promise<string> {
    const fn = httpsCallable(functions, 'refineRepoDescription');
    const result = await fn({ existingText, hint, language });
    return (result.data as { description: string }).description;
}

export interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
}

export async function chatWithReadmeAI(messages: ChatMessage[], currentContent: string): Promise<string> {
    const fn = httpsCallable(functions, 'chatWithReadmeAI', { timeout: 120000 });
    const result = await fn({ messages, currentContent });
    return (result.data as { reply: string }).reply;
}

export async function generateNotebookDescription(notebookId: string, language: 'en' | 'vi' | 'both'): Promise<string> {
    const fn = httpsCallable(functions, 'generateNotebookDescription');
    const result = await fn({ notebookId, language });
    return (result.data as { description: string }).description;
}

export interface ChapterDiff {
    path: string;
    title: string;
    status: 'new' | 'modified' | 'unchanged';
    oldContent?: string;
    newContent?: string;
}

export interface NotebookDiffResult {
    chapterDiffs: ChapterDiff[];
    overviewStatus: 'new' | 'modified' | 'unchanged' | 'absent';
    deletedPaths: string[];
    stats: { newFiles: number; modifiedFiles: number; unchangedFiles: number; deletedFiles: number };
    crossLinkCount?: number;
}

export async function previewGitHubDiff(
    notebookId: string,
    publishMode: 'full' | 'safe',
    links?: { id: string; fromId: string; toId: string; createdAt: number; color?: string }[]
): Promise<NotebookDiffResult> {
    const fn = httpsCallable(functions, 'previewGitHubPublishDiff', { timeout: 120000 });
    const result = await fn({ notebookId, publishMode, links });
    return result.data as NotebookDiffResult;
}

export async function publishNotebookToGitHub(notebookId: string, publishMode: 'full' | 'safe' = 'full', commitMessage?: string): Promise<{ url: string; commitMessage?: string }> {
    const fn = httpsCallable(functions, 'publishNotebookToGitHub', { timeout: 3300000 }); // 55 min client timeout
    const result = await fn({ notebookId, publishMode, commitMessage });
    return result.data as { url: string; commitMessage?: string };
}

export async function unpublishNotebookFromGitHub(notebookId: string): Promise<void> {
    const fn = httpsCallable(functions, 'unpublishNotebookFromGitHub', { timeout: 120000 });
    await fn({ notebookId });
}

export interface GitHubCommit {
    sha: string;
    message: string;
    date: string;
}

export async function getGitHubCommitHistory(): Promise<GitHubCommit[]> {
    const fn = httpsCallable(functions, 'getGitHubCommitHistory', { timeout: 25000 });
    const result = await fn({});
    return (result.data as { commits: GitHubCommit[] }).commits;
}

export async function regenerateReadme(): Promise<void> {
    const fn = httpsCallable(functions, 'regenerateReadme', { timeout: 60000 });
    await fn({});
}
